import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as mediaGatewaySelectors from 'data/mediaGateway/mediaGateway.selectors';
import * as mediaGatewayActions from 'data/mediaGateway/mediaGateway.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as networkSelectors from 'data/network/network.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { AdaptedMediaGateway } from 'data/mediaGateway/mediaGateway.types';
import MediaGatewayForm from 'routes/MediaGateway/components/MediaGatewayForm';
import { FormTemplate } from 'data/template/template.types';
import * as types from 'data/mediaGateway/mediaGateway.types';
import { useMediaGatewayFieldCreateNew } from '../../hook/useMediaGatewayFieldCreateNew';
import { mediaGatewayTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode, adaptTemplateOptions, adaptMediaGatewayData
} from '../../utils/mediaGatewayTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/mediaGatewayTemplateDataToSubmit';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.AdaptedMediaGateway
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  mediaGatewayTemplateDef: adaptTemplateVisibilityMode(mediaGatewayTemplate, 'edit'),
  selectedMediaGateway: mediaGatewaySelectors
    .getSelectedMediaGateway(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedMediaGateway: mediaGatewaySelectors
    .getSelectedMediaGateway(state)[0],
  networks: networkSelectors.getNetworks(state),
  mediaGateway: mediaGatewaySelectors.getMediaGateway(state),
});

const mapDispatch = {
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToMediaGatewayList: mediaGatewayActions.goToMediaGatewayList,
  updateMediaGateway: mediaGatewayActions.updateMediaGateway,
  getMediaGateway: mediaGatewayActions.getMediaGateway,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const MediaGatewayEdit = ({
  history,
  selectedMediaGateway,
  originalSelectedMediaGateway,
  userId,
  userName,
  isAuditEnabled,
  mediaGatewayTemplateDef,
  mediaGateway,
  networks,
  goToMediaGatewayList,
  updateMediaGateway,
  getMediaGateway,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<AdaptedMediaGateway>>({});
  const [template, setTemplate] = useState(mediaGatewayTemplateDef);
  const [handleFieldCreateNew] = useMediaGatewayFieldCreateNew(
    (param) => handleFieldUpdate(param),
  );
  const { t } = useTranslation();

  const id = selectedMediaGateway?.id;

  useEffect(() => {
    if (id) {
      getMediaGateway(String(id));
    }
  }, [id, getMediaGateway]);

  useEffect(() => {
    if (mediaGateway != null && template != null) {
      const clonedMediaGateway = cloneDeep(mediaGateway);
      let clonedTemplate: FormTemplate | undefined = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();
      clonedTemplate = adaptTemplateOptions(clonedTemplate, networks);

      const adaptedMediaGateway = adaptMediaGatewayData(clonedMediaGateway);

      setTemplate(clonedTemplate);
      setFormData(adaptedMediaGateway);
    }
  }, [mediaGateway, id, networks]);

  const getCreateMediaGatewayActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const mediaGatewayData = formRef.current.getValues();
    if (!mediaGatewayData.mediagatewaynumlist) {
      mediaGatewayData.mediagatewaynumlist = [];
    }
    if (mediaGatewayData.insidenat) {
      mediaGatewayData.insidenat = 'YES';
    } else {
      mediaGatewayData.insidenat = 'NO';
    }

    const originalMediagatewaynumlist = originalSelectedMediaGateway?.mediagatewaynumlist;
    const mediagatewaynumId =originalMediagatewaynumlist?.length ? originalMediagatewaynumlist[0]?.id : 0;

    const adaptedMediaGatewayData = {
      ...mediaGatewayData,
      mediagatewaynumlist: mediaGatewayData.mediagatewaynumlist.map((mediagatewaynum) => {
        return {
          callingnum: mediagatewaynum,
          id: mediagatewaynumId,
          cid: 0,
        }
      })
    }

    const payload = {
      data: adaptedMediaGatewayData,
      auditDetails: getAuditDetailsToSubmit(
        mediaGatewayData,
        originalSelectedMediaGateway,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateMediaGatewayActionPayload();
    if (payload && mediaGateway != null) {
      updateMediaGateway(String(mediaGateway?.id), payload, history);
    }
  };

  const handleCancel = () => goToMediaGatewayList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as AdaptedMediaGateway);
    const clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <MediaGatewayForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
          onFieldCreateNew={handleFieldCreateNew}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (mediaGateway == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="MediaGatewayEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('mediaGateway.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(MediaGatewayEdit));
